<template>
  <div class="content">
    <h2 class="content-heading">
      <i class="fa fa-sync-alt mr-2"></i>
      FFINS API Синхронизация
    </h2>

    <!-- Sync Form -->
    <div class="block block-rounded">
      <div class="block-header block-header-default">
        <h3 class="block-title">Запуск синхронизации</h3>
        <div class="block-options">
          <button
            type="button"
            class="btn-block-option"
            data-toggle="block-option"
            data-action="content_toggle"
          >
            <i class="si si-arrow-up"></i>
          </button>
        </div>
      </div>
      <div class="block-content">
        <form @submit.prevent="startSync">
          <div class="row">
            <div class="col-md-6">
              <div class="form-group">
                <label for="sync-type">Тип синхронизации</label>
                <select
                  class="form-control"
                  id="sync-type"
                  v-model="syncForm.type"
                  :class="{ 'is-invalid': syncErrors.type }"
                >
                  <option value="debts">Договоры</option>
                  <option value="payments">Платежи</option>
                </select>
                <div v-if="syncErrors.type" class="invalid-feedback">
                  {{ syncErrors.type[0] }}
                </div>
              </div>
            </div>
            <div class="col-md-6">
              <div class="form-group">
                <label for="sync-date">Дата цессии</label>
                <select
                  class="form-control"
                  id="sync-date"
                  v-model="syncForm.date"
                  :class="{ 'is-invalid': syncErrors.date }"
                  required
                  :disabled="isLoadingDates"
                >
                  <option value="" disabled>
                    {{ isLoadingDates ? 'Загрузка дат...' : 'Выберите дату цессии' }}
                  </option>
                  <option
                    v-for="date in cessionDates"
                    :key="date"
                    :value="date"
                  >
                    {{ formatCessionDate(date) }}
                  </option>
                </select>
                <div v-if="syncErrors.date" class="invalid-feedback">
                  {{ syncErrors.date[0] }}
                </div>
              </div>
            </div>
          </div>
          <div class="row">
            <div class="col-md-12">
              <div class="form-group">
                <div class="custom-control custom-checkbox">
                  <input
                    type="checkbox"
                    class="custom-control-input"
                    id="sync-async"
                    v-model="syncForm.async"
                  />
                  <label class="custom-control-label" for="sync-async">
                    Запустить в фоновом режиме (асинхронно)
                  </label>
                </div>
              </div>
            </div>
          </div>
          <div class="alert alert-info" v-if="syncForm.type === 'payments'">
            <small>
              <strong>Информация:</strong> Будут получены все договоры за указанную дату, затем загружены платежи по
              этим договорам.
            </small>
          </div>
          <div class="alert alert-info" v-else>
            <small>
              <strong>Примечание:</strong> БИН страхователя и БИН коллектора устанавливаются автоматически из
              конфигурации сервера.
            </small>
          </div>
          <button
            type="submit"
            class="btn btn-primary"
            :disabled="isSyncing"
          >
            <i class="fa fa-spinner fa-spin" v-if="isSyncing"></i>
            <i class="fa fa-sync-alt" v-else></i>
            {{ isSyncing ? 'Синхронизация...' : 'Запустить синхронизацию' }}
          </button>
        </form>
      </div>
    </div>

    <!-- Statistics -->
    <div class="row" v-if="statistics">
      <div class="col-md-3">
        <div class="block block-rounded">
          <div class="block-content block-content-full">
            <div class="font-size-sm font-w600 text-uppercase text-muted">
              Всего импортов
            </div>
            <div class="font-size-h2 font-w400 text-dark">
              {{ statistics.total_imports }}
            </div>
          </div>
        </div>
      </div>
      <div class="col-md-3">
        <div class="block block-rounded">
          <div class="block-content block-content-full">
            <div class="font-size-sm font-w600 text-uppercase text-muted">
              Завершено успешно
            </div>
            <div class="font-size-h2 font-w400 text-success">
              {{ statistics.completed }}
            </div>
          </div>
        </div>
      </div>
      <div class="col-md-3">
        <div class="block block-rounded">
          <div class="block-content block-content-full">
            <div class="font-size-sm font-w600 text-uppercase text-muted">
              Создано записей
            </div>
            <div class="font-size-h2 font-w400 text-info">
              {{ statistics.total_debtors_created }}
            </div>
          </div>
        </div>
      </div>
      <div class="col-md-3">
        <div class="block block-rounded">
          <div class="block-content block-content-full">
            <div class="font-size-sm font-w600 text-uppercase text-muted">
              Обновлено записей
            </div>
            <div class="font-size-h2 font-w400 text-primary">
              {{ statistics.total_debtors_updated }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Import History -->
    <div class="block block-rounded">
      <div class="block-header block-header-default">
        <h3 class="block-title">История синхронизаций</h3>
        <div class="block-options">
          <button
            type="button"
            class="btn btn-sm btn-light"
            @click="fetchImports"
          >
            <i class="fa fa-sync-alt"></i> Обновить
          </button>
        </div>
      </div>
      <div class="block-content">
        <div class="table-responsive">
          <table class="table table-striped table-vcenter">
            <thead>
            <tr>
              <th>ID</th>
              <th>Дата цессии</th>
              <th>БИН</th>
              <th>Тип</th>
              <th>Статус</th>
              <th>Всего записей</th>
              <th>Обработано</th>
              <th>Создано</th>
              <th>Обновлено</th>
              <th>Ошибок</th>
              <th>Запущено</th>
              <th>Действия</th>
            </tr>
            </thead>
            <tbody>
            <tr v-if="isLoading">
              <td colspan="12" class="text-center">
                <i class="fa fa-spinner fa-spin"></i> Загрузка...
              </td>
            </tr>
            <tr v-else-if="imports.length === 0">
              <td colspan="12" class="text-center text-muted">
                Нет данных для отображения
              </td>
            </tr>
            <tr v-else v-for="import_ in imports" :key="import_.id">
              <td>{{ import_.id }}</td>
              <td>{{ import_.request_date | dateFormat }}</td>
              <td>
                <small>{{ import_.request_bin }}</small>
              </td>
              <td>
                  <span class="badge badge-info">
                    {{ getImportTypeText(import_) }}
                  </span>
              </td>
              <td>
                  <span
                    class="badge"
                    :class="getStatusClass(import_.status)"
                  >
                    {{ getStatusText(import_.status) }}
                  </span>
              </td>
              <td>{{ import_.total_records }}</td>
              <td>{{ import_.processed_count }}</td>
              <td>{{ import_.created_count }}</td>
              <td>{{ import_.updated_count }}</td>
              <td>
                  <span
                    :class="import_.failed_count > 0 ? 'text-danger font-weight-bold' : ''"
                  >
                    {{ import_.failed_count }}
                  </span>
              </td>
              <td>
                <small>{{ formatDate(import_.created_at) }}</small>
              </td>
              <td>
                <router-link
                  :to="{ name: 'adminFFinsDetail', params: { id: import_.id } }"
                  class="btn btn-sm btn-info"
                >
                  <i class="fa fa-eye"></i> Детали
                </router-link>
              </td>
            </tr>
            </tbody>
          </table>
        </div>

        <!-- Pagination -->
        <div v-if="pagination.total > pagination.per_page" class="row">
          <div class="col-md-12">
            <nav>
              <ul class="pagination justify-content-center">
                <li
                  class="page-item"
                  :class="{ disabled: pagination.current_page === 1 }"
                >
                  <a
                    class="page-link"
                    href="#"
                    @click.prevent="changePage(pagination.current_page - 1)"
                  >
                    Предыдущая
                  </a>
                </li>
                <li
                  v-for="page in visiblePages"
                  :key="page"
                  class="page-item"
                  :class="{ active: pagination.current_page === page }"
                >
                  <a class="page-link" href="#" @click.prevent="changePage(page)">
                    {{ page }}
                  </a>
                </li>
                <li
                  class="page-item"
                  :class="{
                    disabled: pagination.current_page === pagination.last_page,
                  }"
                >
                  <a
                    class="page-link"
                    href="#"
                    @click.prevent="changePage(pagination.current_page + 1)"
                  >
                    Следующая
                  </a>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'FFinsSync',
  data() {
    return {
      syncForm: {
        type: 'debts',
        date: '',
        async: true,
      },
      syncErrors: {},
      isSyncing: false,
      isLoading: false,
      isLoadingDates: false,
      cessionDates: [],
      imports: [],
      statistics: null,
      pagination: {
        current_page: 1,
        per_page: 15,
        total: 0,
        last_page: 1,
      },
    };
  },
  computed: {
    visiblePages() {
      const pages = [];
      const current = this.pagination.current_page;
      const last = this.pagination.last_page;
      const delta = 2;

      for (let i = Math.max(2, current - delta); i <= Math.min(last - 1, current + delta); i++) {
        pages.push(i);
      }

      if (current - delta > 2) {
        pages.unshift('...');
      }
      if (current + delta < last - 1) {
        pages.push('...');
      }

      pages.unshift(1);
      if (last > 1) {
        pages.push(last);
      }

      return pages;
    },
  },
  mounted() {
    this.fetchCessionDates();
    this.fetchImports();
    this.fetchStatistics();
  },
  methods: {
    async fetchCessionDates() {
      this.isLoadingDates = true;

      try {
        const response = await axios.get('/ffins/cession-dates');

        if (response.data.success && response.data.data) {
          this.cessionDates = response.data.data;

          // Автоматически выбираем последнюю дату если есть
          if (this.cessionDates.length > 0) {
            this.syncForm.date = this.cessionDates[this.cessionDates.length - 1];
          }
        }
      } catch (error) {
        console.error('Error fetching cession dates:', error);
        window.Swal({
          title: 'Ошибка!',
          text: 'Не удалось загрузить даты цессий',
          icon: 'error',
        });
      } finally {
        this.isLoadingDates = false;
      }
    },
    async startSync() {
      this.isSyncing = true;
      this.syncErrors = {};

      try {
        const response = await axios.post('/ffins/sync', this.syncForm);

        if (response.data.success) {
          window.Swal({
            title: 'Успех!',
            text: response.data.message,
            icon: 'success',
          });

          this.fetchImports();
          this.fetchStatistics();
        }
      } catch (error) {
        if (error.response && error.response.status === 422) {
          this.syncErrors = error.response.data.errors;
        } else {
          window.Swal({
            title: 'Ошибка!',
            text: (error.response && error.response.data && error.response.data.message) || 'Произошла ошибка при запуске синхронизации',
            icon: 'error',
          });
        }
      } finally {
        this.isSyncing = false;
      }
    },
    async fetchImports(page = 1) {
      this.isLoading = true;

      try {
        const response = await axios.get('/ffins/imports', {
          params: {
            page,
            per_page: this.pagination.per_page,
          },
        });

        if (response.data.success) {
          this.imports = response.data.data.data;
          this.pagination = {
            current_page: response.data.data.current_page,
            per_page: response.data.data.per_page,
            total: response.data.data.total,
            last_page: response.data.data.last_page,
          };
        }
      } catch (error) {
        console.error('Error fetching imports:', error);
      } finally {
        this.isLoading = false;
      }
    },
    async fetchStatistics() {
      try {
        const response = await axios.get('/ffins/statistics');
        if (response.data.success) {
          this.statistics = response.data.data;
        }
      } catch (error) {
        console.error('Error fetching statistics:', error);
      }
    },
    changePage(page) {
      if (page >= 1 && page <= this.pagination.last_page) {
        this.fetchImports(page);
      }
    },
    getImportTypeText(import_) {
      const type = import_.import_type || import_.type;
      if (type === 'payments') {
        return 'Платежи';
      }
      if (type === 'debts') {
        return 'Договоры';
      }
      return type || '-';
    },
    getStatusClass(status) {
      const classes = {
        pending: 'badge-secondary',
        processing: 'badge-info',
        completed: 'badge-success',
        failed: 'badge-danger',
      };
      return classes[status] || 'badge-secondary';
    },
    getStatusText(status) {
      const texts = {
        pending: 'Ожидание',
        processing: 'В процессе',
        completed: 'Завершено',
        failed: 'Ошибка',
      };
      return texts[status] || status;
    },
    formatDate(date) {
      if (!date) return '-';
      const d = new Date(date);
      const options = {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      };
      return d.toLocaleString('ru-RU', options);
    },
    formatCessionDate(date) {
      if (!date) return '-';
      // Парсим дату формата dd.MM.yyyy
      const parts = date.split('.');
      if (parts.length === 3) {
        const day = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10) - 1; // месяцы в JS начинаются с 0
        const year = parseInt(parts[2], 10);
        const d = new Date(year, month, day);

        const options = {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        };
        return d.toLocaleDateString('ru-RU', options);
      }
      return date;
    },
  },
};
</script>

<style scoped>
.block {
  margin-bottom: 20px;
}
</style>

